import { useCallback, useEffect, useRef, useState } from "react";
import {
  setConditionEvaluator,
  type ConditionState,
} from "./conditional-evaluator";
import type { DynamicNodeContextValue } from "./dynamic-node-context";

/**
 * The data-key registration machinery shared by the dynamic-node providers
 * (editor and template preview). Nodes self-register the data keys they need
 * (the provider can't see the editor). We keep the live registrations in a ref
 * and recompute the requested union on a debounce so a burst of mounts (e.g.
 * initial render) collapses into a single state update / refetch.
 */
export const useDataKeyRegistration = (): {
  requestedKeys: string[];
  registerKeys: (id: string, keys: string[]) => void;
  unregisterKeys: (id: string) => void;
} => {
  const registrationsRef = useRef<Map<string, string[]>>(new Map());
  const [requestedKeys, setRequestedKeys] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const recomputeRequestedKeys = useCallback(() => {
    const union = new Set<string>();
    for (const keys of registrationsRef.current.values()) {
      for (const key of keys) {
        if (key) {
          union.add(key);
        }
      }
    }
    const next = Array.from(union).sort();
    // Guard against needless state updates (which would refetch / re-render).
    setRequestedKeys((prev) =>
      prev.join(" ") === next.join(" ") ? prev : next
    );
  }, []);

  const scheduleRecompute = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(recomputeRequestedKeys, 250);
  }, [recomputeRequestedKeys]);

  const registerKeys = useCallback(
    (id: string, keys: string[]) => {
      registrationsRef.current.set(id, keys);
      scheduleRecompute();
    },
    [scheduleRecompute]
  );

  const unregisterKeys = useCallback(
    (id: string) => {
      registrationsRef.current.delete(id);
      scheduleRecompute();
    },
    [scheduleRecompute]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    []
  );

  return { requestedKeys, registerKeys, unregisterKeys };
};

/**
 * Publishes the provider's evaluator + a resolution-state resolver so the
 * conditional table *row* and *cell* decoration plugins (which live outside
 * React) can hide false rows in the read-only preview and colour each row/cell
 * edge green/yellow/red by resolution — like the conditional chip. The resolver
 * is keyed on the keys a condition references (each decoration collects its own
 * keys). Refreshes whenever the resolved data changes.
 */
export const useConditionEvaluatorBridge = (
  value: DynamicNodeContextValue
): void => {
  useEffect(() => {
    const resolveState = (keys: string[]): ConditionState => {
      if (keys.length === 0) {
        return "empty";
      }
      if (
        keys.some(
          (key) =>
            !value.resolveDataKey(key).isUnresolved ||
            !value.resolveContext(key).isUnresolved
        )
      ) {
        return "resolved";
      }
      if (keys.some((key) => value.isKeyPending(key))) {
        return "loading";
      }
      return "unresolved";
    };
    setConditionEvaluator(value.evaluateHandlebars, resolveState);
    return () => setConditionEvaluator(null);
  }, [value]);
};
